/**
 * Surface textures, drawn at runtime.
 *
 * Every wall in the world used to be one flat colour, which is why the towns
 * read as a pile of coloured boxes no matter how carefully they were laid out.
 * Nothing else on this list — more buildings, more props, better lighting —
 * moves the needle as far as putting a surface on a surface.
 *
 * Drawn into a canvas rather than downloaded:
 *   - nothing to ship, so the bundle does not grow at all;
 *   - nothing to licence, which is the same reason there are no imported models;
 *   - one texture per surface for the whole world, built once and shared, so
 *     fifty buildings cost one 256×256 upload to the GPU between them.
 *
 * The randomness is seeded. An unseeded Math.random would reshuffle every stone
 * in the town on each page load, and "the wall looked different yesterday" is a
 * bug report nobody can act on.
 */

import type { Scene } from '@babylonjs/core/scene.js';
import { DynamicTexture } from '@babylonjs/core/Materials/Textures/dynamicTexture.js';
import type { Surface } from '@emberreach/shared';

/** Deterministic, cheap, and good enough for speckle. */
function seeded(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (Math.imul(state, 1664525) + 1013904223) >>> 0;
    return state / 4294967296;
  };
}

const SIZE = 256;

/** Fills the whole canvas with a base colour. */
function base(ctx: CanvasRenderingContext2D, hex: string): void {
  ctx.fillStyle = hex;
  ctx.fillRect(0, 0, SIZE, SIZE);
}

/** Scatters translucent specks — the cheapest way to kill a flat fill. */
function speckle(
  ctx: CanvasRenderingContext2D,
  rand: () => number,
  count: number,
  maxSize: number,
  light: string,
  dark: string
): void {
  for (let i = 0; i < count; i += 1) {
    ctx.fillStyle = rand() > 0.5 ? light : dark;
    const size = 1 + rand() * maxSize;
    ctx.fillRect(rand() * SIZE, rand() * SIZE, size, size);
  }
}

/**
 * Courses of stone or brick.
 *
 * Each block is nudged a shade lighter or darker than its neighbour, and a
 * highlight along the top edge does the work a bump map would: at any distance
 * a player actually stands, that one line is what makes it read as stacked
 * rather than printed.
 */
function masonry(
  ctx: CanvasRenderingContext2D,
  rand: () => number,
  mortar: string,
  courses: number,
  perCourse: number
): void {
  const rowHeight = SIZE / courses;
  ctx.fillStyle = mortar;
  ctx.fillRect(0, 0, SIZE, SIZE);

  for (let row = 0; row < courses; row += 1) {
    // Every second course is offset, or the joints line up into columns and it
    // stops looking like anything anybody built.
    const offset = row % 2 === 0 ? 0 : SIZE / perCourse / 2;
    for (let col = -1; col < perCourse + 1; col += 1) {
      const x = col * (SIZE / perCourse) + offset + 1.5;
      const y = row * rowHeight + 1.5;
      const w = SIZE / perCourse - 3;
      const h = rowHeight - 3;
      const shade = 0.86 + rand() * 0.28;
      ctx.fillStyle = `rgba(255,255,255,${(shade - 0.86) * 0.5})`;
      ctx.fillRect(x, y, w, h);
      ctx.fillStyle = shade > 1 ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.13)';
      ctx.fillRect(x, y, w, h);
      // Lit top edge, shaded bottom.
      ctx.fillStyle = 'rgba(255,255,255,0.14)';
      ctx.fillRect(x, y, w, 1.5);
      ctx.fillStyle = 'rgba(0,0,0,0.16)';
      ctx.fillRect(x, y + h - 1.5, w, 1.5);
    }
  }
}

/** Vertical boards with a bit of grain, for anything made of wood. */
function planks(ctx: CanvasRenderingContext2D, rand: () => number, count: number): void {
  const width = SIZE / count;
  for (let i = 0; i < count; i += 1) {
    const x = i * width;
    ctx.fillStyle = rand() > 0.5 ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.09)';
    ctx.fillRect(x, 0, width, SIZE);
    // The gap between boards.
    ctx.fillStyle = 'rgba(0,0,0,0.34)';
    ctx.fillRect(x, 0, 1.5, SIZE);
    // Grain: long thin streaks that follow the board.
    for (let g = 0; g < 7; g += 1) {
      ctx.fillStyle = 'rgba(0,0,0,0.07)';
      ctx.fillRect(x + rand() * width, rand() * SIZE, 1, 20 + rand() * 60);
    }
  }
}

function drawSurface(ctx: CanvasRenderingContext2D, surface: Surface, hex: string): void {
  // One seed per surface name, so stone is always the same stone.
  let seed = 7;
  for (let i = 0; i < surface.length; i += 1) seed = seed * 31 + surface.charCodeAt(i);
  const rand = seeded(seed);

  base(ctx, hex);

  switch (surface) {
    case 'stone':
      masonry(ctx, rand, 'rgba(0,0,0,0.28)', 7, 5);
      speckle(ctx, rand, 260, 2, 'rgba(255,255,255,0.07)', 'rgba(0,0,0,0.09)');
      break;

    case 'darkStone':
      masonry(ctx, rand, 'rgba(0,0,0,0.4)', 9, 6);
      break;

    case 'timber':
    case 'darkTimber':
      planks(ctx, rand, 7);
      break;

    case 'plaster':
      // Mottled, plus the horizontal timber banding of a rendered wall.
      speckle(ctx, rand, 900, 3, 'rgba(255,255,255,0.09)', 'rgba(0,0,0,0.06)');
      for (const y of [0, SIZE - 10]) {
        ctx.fillStyle = 'rgba(70,50,32,0.55)';
        ctx.fillRect(0, y, SIZE, 10);
      }
      break;

    case 'thatch':
      // Overlapping strokes leaning one way — the leaning is what makes it
      // straw rather than carpet.
      for (let i = 0; i < 900; i += 1) {
        const x = rand() * SIZE;
        const y = rand() * SIZE;
        ctx.strokeStyle = rand() > 0.5 ? 'rgba(255,235,180,0.16)' : 'rgba(60,44,20,0.22)';
        ctx.lineWidth = 1 + rand();
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.lineTo(x + 3 + rand() * 5, y + 12 + rand() * 10);
        ctx.stroke();
      }
      // Courses, tied down every so often.
      for (let y = 0; y < SIZE; y += SIZE / 5) {
        ctx.fillStyle = 'rgba(0,0,0,0.16)';
        ctx.fillRect(0, y, SIZE, 3);
      }
      break;

    case 'tile': {
      // Scalloped rows: a half-circle per tile, staggered.
      const rows = 8;
      const perRow = 9;
      const h = SIZE / rows;
      const w = SIZE / perRow;
      for (let row = 0; row < rows; row += 1) {
        for (let col = -1; col < perRow + 1; col += 1) {
          const x = col * w + (row % 2 === 0 ? 0 : w / 2);
          const y = row * h;
          ctx.fillStyle = rand() > 0.5 ? 'rgba(255,255,255,0.09)' : 'rgba(0,0,0,0.10)';
          ctx.beginPath();
          ctx.moveTo(x, y + h);
          ctx.lineTo(x, y + h * 0.45);
          ctx.arc(x + w / 2, y + h * 0.45, w / 2, Math.PI, 0);
          ctx.lineTo(x + w, y + h);
          ctx.closePath();
          ctx.fill();
          ctx.strokeStyle = 'rgba(0,0,0,0.26)';
          ctx.lineWidth = 1;
          ctx.stroke();
        }
      }
      break;
    }

    case 'slate':
      masonry(ctx, rand, 'rgba(0,0,0,0.36)', 11, 4);
      break;

    case 'metal':
      for (let i = 0; i < 90; i += 1) {
        ctx.fillStyle = rand() > 0.5 ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.08)';
        ctx.fillRect(rand() * SIZE, 0, 1 + rand() * 3, SIZE);
      }
      break;

    case 'road':
      // Cobbles: rounded, irregular, with the gaps showing through.
      ctx.fillStyle = 'rgba(0,0,0,0.3)';
      ctx.fillRect(0, 0, SIZE, SIZE);
      for (let row = 0; row < 10; row += 1) {
        for (let col = -1; col < 11; col += 1) {
          const x = col * (SIZE / 10) + (row % 2 === 0 ? 0 : SIZE / 20) + rand() * 3;
          const y = row * (SIZE / 10) + rand() * 3;
          const shade = rand();
          ctx.fillStyle = shade > 0.5 ? 'rgba(255,255,255,0.16)' : 'rgba(0,0,0,0.10)';
          ctx.fillRect(x + 2, y + 2, SIZE / 10 - 4, SIZE / 10 - 4);
          // Lit top edge on each stone, so the road reads as laid, not painted.
          ctx.fillStyle = 'rgba(255,255,255,0.13)';
          ctx.fillRect(x + 2, y + 2, SIZE / 10 - 4, 2);
        }
      }
      break;

    case 'grass':
      /* Patches first, blades over the top.
         A ground plane is two triangles: it has no geometry to catch the light,
         so every bit of variation on it has to come from here. Built out of
         fillRect alone rather than ellipses and strokes — deliberately, because
         this is the one surface where a silently unsupported canvas call would
         leave a flat green field and look exactly like a texture that had never
         been applied. */
      for (let i = 0; i < 150; i += 1) {
        ctx.fillStyle = rand() > 0.45 ? 'rgba(255,255,255,0.30)' : 'rgba(0,0,0,0.26)';
        const w = 14 + rand() * 44;
        const h = 10 + rand() * 30;
        ctx.fillRect(rand() * SIZE - w / 2, rand() * SIZE - h / 2, w, h);
      }
      // Blades: short vertical marks, two shades, dense enough to read as turf.
      for (let i = 0; i < 2600; i += 1) {
        ctx.fillStyle = rand() > 0.5 ? 'rgba(255,255,255,0.34)' : 'rgba(0,0,0,0.30)';
        ctx.fillRect(rand() * SIZE, rand() * SIZE, 1.6, 4 + rand() * 7);
      }
      break;

    case 'sand':
    case 'ash':
      speckle(ctx, rand, 2600, 2, 'rgba(255,255,255,0.10)', 'rgba(0,0,0,0.09)');
      break;

    // Water and ember are lit and animated by their material rather than
    // patterned; a texture on either would fight what makes them read.
    case 'water':
    case 'ember':
      break;
  }
}

/**
 * A texture for a surface, built once per scene.
 *
 * `repeat` is how many times it tiles across a mesh's 0..1 UV range. Walls want
 * a small number; the ground is two hundred metres across and wants a large one,
 * or every blade of grass is the size of a house.
 */
export function surfaceTexture(
  scene: Scene,
  surface: Surface,
  repeat: number
): DynamicTexture | null {
  if (surface === 'water' || surface === 'ember') return null;

  const name = `tex-${surface}-${repeat}`;
  const existing = scene.getTextureByName(name);
  if (existing instanceof DynamicTexture) return existing;

  const texture = new DynamicTexture(name, { width: SIZE, height: SIZE }, scene, true);
  const ctx = texture.getContext() as unknown as CanvasRenderingContext2D;

  // Drawn on top of the material's own diffuse colour, so the palette in
  // renderer.ts stays the single place a surface's colour is decided.
  drawSurface(ctx, surface, '#ffffff');
  texture.update(false);

  texture.uScale = repeat;
  texture.vScale = repeat;
  texture.hasAlpha = false;

  /* Anisotropic filtering, and it matters most exactly where it is least
     obvious: a ground plane is seen almost edge-on, and without this the mip
     chain blurs everything past a few metres into one flat colour — which is
     indistinguishable from having applied no texture at all. */
  texture.anisotropicFilteringLevel = repeat > 8 ? 8 : 4;
  return texture;
}
